import { DomainError, assertFound } from './errors.js'
import { validateFinding } from './findings.js'
import { cellKey, type NormalizedBoard } from './normalized.js'
import type { Operation } from './operations.js'
import type { Finding } from './findings.js'
import type { Board, Card, Column, ColumnGroup, Row, Swimlane } from './schemas.js'

export interface BoardChanges {
  board: Board | null
  groups: { upserted: ColumnGroup[]; deleted: string[] }
  columns: { upserted: Column[]; deleted: string[] }
  swimlanes: { upserted: Swimlane[]; deleted: string[] }
  rows: { upserted: Row[]; deleted: string[] }
  cards: { upserted: Card[]; deleted: string[] }
  /** Optional: results recorded before findings existed are replayed without it. */
  findings?: { upserted: Finding[]; deleted: string[] }
}

export interface ApplyResult {
  state: NormalizedBoard
  changes: BoardChanges
}

export interface ApplyOptions {
  /** Timestamp stamped on touched entities. Injected so applying is deterministic in tests. */
  now?: string
  /** The authoritative store increments once per batch; optimistic client applies do not. */
  bumpRevision?: boolean
}

type EntityKind = 'groups' | 'columns' | 'swimlanes' | 'rows' | 'cards' | 'findings'

/**
 * Copy-on-write view over a NormalizedBoard. Only the maps and ordered lists that an
 * operation actually touches get new references, so unrelated cells and previews keep
 * their identity and skip rerendering.
 */
class Draft {
  private next: NormalizedBoard
  private copiedMaps = new Set<EntityKind>()
  private copiedRowIndex = false
  private copiedCellIndex = false
  private boardChanged = false
  private readonly upserted: Record<EntityKind, Set<string>> = {
    groups: new Set(),
    columns: new Set(),
    swimlanes: new Set(),
    rows: new Set(),
    cards: new Set(),
    findings: new Set(),
  }
  private readonly removed: Record<EntityKind, Set<string>> = {
    groups: new Set(),
    columns: new Set(),
    swimlanes: new Set(),
    rows: new Set(),
    cards: new Set(),
    findings: new Set(),
  }

  constructor(base: NormalizedBoard) {
    // A board normalized before findings existed has no map yet.
    this.next = { ...base, findings: base.findings ?? {} }
  }

  get state(): NormalizedBoard {
    return this.next
  }

  private mapOf<K extends EntityKind>(kind: K): NormalizedBoard[K] {
    if (!this.copiedMaps.has(kind)) {
      ;(this.next as unknown as Record<string, unknown>)[kind] = { ...this.next[kind] }
      this.copiedMaps.add(kind)
    }
    return this.next[kind]
  }

  private track(kind: EntityKind, id: string, action: 'upsert' | 'remove'): void {
    if (action === 'upsert') {
      this.removed[kind].delete(id)
      this.upserted[kind].add(id)
    } else {
      this.upserted[kind].delete(id)
      this.removed[kind].add(id)
    }
  }

  // --- entity writes -------------------------------------------------------

  putGroup(value: ColumnGroup): void {
    this.mapOf('groups')[value.id] = value
    this.track('groups', value.id, 'upsert')
  }

  putColumn(value: Column): void {
    this.mapOf('columns')[value.id] = value
    this.track('columns', value.id, 'upsert')
  }

  putSwimlane(value: Swimlane): void {
    this.mapOf('swimlanes')[value.id] = value
    this.track('swimlanes', value.id, 'upsert')
  }

  putRow(value: Row): void {
    this.mapOf('rows')[value.id] = value
    this.track('rows', value.id, 'upsert')
  }

  putCard(value: Card): void {
    this.mapOf('cards')[value.id] = value
    this.track('cards', value.id, 'upsert')
  }

  putFinding(value: Finding): void {
    this.mapOf('findings')[value.id] = value
    this.track('findings', value.id, 'upsert')
  }

  drop(kind: EntityKind, id: string): void {
    delete (this.mapOf(kind) as Record<string, unknown>)[id]
    this.track(kind, id, 'remove')
  }

  setBoard(board: Board): void {
    this.next.board = board
    this.boardChanged = true
  }

  // --- ordered lists -------------------------------------------------------

  setGroupIds(ids: string[]): void {
    this.next.groupIds = ids
  }

  setColumnIds(ids: string[]): void {
    this.next.columnIds = ids
    const map = this.mapOf('columns')
    ids.forEach((id, position) => {
      const current = map[id]
      if (current && current.position !== position) {
        map[id] = { ...current, position }
        this.track('columns', id, 'upsert')
      }
    })
  }

  setSwimlaneIds(ids: string[]): void {
    this.next.swimlaneIds = ids
    const map = this.mapOf('swimlanes')
    ids.forEach((id, position) => {
      const current = map[id]
      if (current && current.position !== position) {
        map[id] = { ...current, position }
        this.track('swimlanes', id, 'upsert')
      }
    })
  }

  setRowIds(swimlaneId: string, ids: string[]): void {
    if (!this.copiedRowIndex) {
      this.next.rowIdsBySwimlane = { ...this.next.rowIdsBySwimlane }
      this.copiedRowIndex = true
    }
    this.next.rowIdsBySwimlane[swimlaneId] = ids
    const map = this.mapOf('rows')
    ids.forEach((id, position) => {
      const current = map[id]
      if (current && (current.position !== position || current.swimlaneId !== swimlaneId)) {
        map[id] = { ...current, position, swimlaneId }
        this.track('rows', id, 'upsert')
      }
    })
  }

  dropRowIds(swimlaneId: string): void {
    if (!this.copiedRowIndex) {
      this.next.rowIdsBySwimlane = { ...this.next.rowIdsBySwimlane }
      this.copiedRowIndex = true
    }
    delete this.next.rowIdsBySwimlane[swimlaneId]
  }

  setCardIds(rowId: string, columnId: string, ids: string[]): void {
    if (!this.copiedCellIndex) {
      this.next.cardIdsByCell = { ...this.next.cardIdsByCell }
      this.copiedCellIndex = true
    }
    const key = cellKey(rowId, columnId)
    if (ids.length === 0) delete this.next.cardIdsByCell[key]
    else this.next.cardIdsByCell[key] = ids
    const map = this.mapOf('cards')
    ids.forEach((id, position) => {
      const current = map[id]
      if (current && (current.position !== position || current.rowId !== rowId || current.columnId !== columnId)) {
        map[id] = { ...current, position, rowId, columnId }
        this.track('cards', id, 'upsert')
      }
    })
  }

  // --- result --------------------------------------------------------------

  changes(): BoardChanges {
    const collect = <K extends EntityKind>(kind: K) => ({
      upserted: [...this.upserted[kind]]
        .map((id) => this.next[kind][id])
        .filter((value): value is NonNullable<typeof value> => value !== undefined),
      deleted: [...this.removed[kind]],
    })
    return {
      board: this.boardChanged ? this.next.board : null,
      groups: collect('groups') as BoardChanges['groups'],
      columns: collect('columns') as BoardChanges['columns'],
      swimlanes: collect('swimlanes') as BoardChanges['swimlanes'],
      rows: collect('rows') as BoardChanges['rows'],
      cards: collect('cards') as BoardChanges['cards'],
      findings: collect('findings') as BoardChanges['findings'],
    }
  }

  get touchedAnything(): boolean {
    if (this.boardChanged) return true
    for (const kind of ['groups', 'columns', 'swimlanes', 'rows', 'cards', 'findings'] as const) {
      if (this.upserted[kind].size > 0 || this.removed[kind].size > 0) return true
    }
    return false
  }
}

/** Insert `id` into `ids` before `beforeId`, or append when `beforeId` is null. */
function insertBefore(ids: readonly string[], id: string, beforeId: string | null, listLabel: string): string[] {
  const next = ids.filter((existing) => existing !== id)
  if (beforeId === null) {
    next.push(id)
    return next
  }
  if (beforeId === id) {
    throw new DomainError('invalid_operation', 'An entity cannot be positioned relative to itself')
  }
  const index = next.indexOf(beforeId)
  if (index === -1) {
    throw new DomainError('invalid_reference', `Destination ${beforeId} is not in ${listLabel}`)
  }
  next.splice(index, 0, id)
  return next
}

function sameBoard(boardId: string, entityBoardId: string, kind: string): void {
  if (boardId !== entityBoardId) {
    throw new DomainError('invalid_reference', `${kind} belongs to a different board`)
  }
}

export function applyOperations(
  state: NormalizedBoard,
  operations: readonly Operation[],
  options: ApplyOptions = {},
): ApplyResult {
  const now = options.now ?? new Date().toISOString()
  const draft = new Draft(state)

  for (const operation of operations) {
    applyOne(draft, operation, now)
  }

  if (options.bumpRevision && draft.touchedAnything) {
    const board = draft.state.board
    draft.setBoard({ ...board, revision: board.revision + 1, updatedAt: now })
  }

  return { state: draft.state, changes: draft.changes() }
}

function applyOne(draft: Draft, operation: Operation, now: string): void {
  const s = draft.state
  const boardId = s.board.id

  switch (operation.type) {
    case 'board.rename': {
      draft.setBoard({ ...s.board, name: operation.name, updatedAt: now })
      return
    }
    case 'board.setContext': {
      draft.setBoard({ ...s.board, context: operation.context, updatedAt: now })
      return
    }

    // --- column groups -----------------------------------------------------
    case 'group.create': {
      const group = operation.group
      sameBoard(boardId, group.boardId, 'Column group')
      if (s.groups[group.id]) throw new DomainError('invalid_operation', 'Column group already exists')
      draft.putGroup(group)
      draft.setGroupIds([...s.groupIds, group.id])
      return
    }
    case 'group.rename': {
      const group = assertFound(s.groups[operation.id], 'Column group', operation.id)
      draft.putGroup({ ...group, title: operation.title })
      return
    }
    case 'group.setNotes': {
      const group = assertFound(s.groups[operation.id], 'Column group', operation.id)
      draft.putGroup({ ...group, notes: operation.notes })
      return
    }
    case 'group.delete': {
      assertFound(s.groups[operation.id], 'Column group', operation.id)
      // Deleting a group unassigns its columns; it never deletes them.
      for (const columnId of s.columnIds) {
        const column = s.columns[columnId]!
        if (column.groupId === operation.id) draft.putColumn({ ...column, groupId: null })
      }
      draft.drop('groups', operation.id)
      draft.setGroupIds(s.groupIds.filter((id) => id !== operation.id))
      return
    }

    // --- columns -----------------------------------------------------------
    case 'column.create': {
      const input = operation.column
      sameBoard(boardId, input.boardId, 'Column')
      if (s.columns[input.id]) throw new DomainError('invalid_operation', 'Column already exists')
      if (input.groupId !== null && !s.groups[input.groupId]) {
        throw new DomainError('invalid_reference', 'Column group does not exist')
      }
      draft.putColumn({ ...input, position: 0 })
      draft.setColumnIds(insertBefore(s.columnIds, input.id, operation.beforeId, 'the column order'))
      return
    }
    case 'column.rename': {
      const column = assertFound(s.columns[operation.id], 'Column', operation.id)
      draft.putColumn({ ...column, title: operation.title })
      return
    }
    case 'column.setNotes': {
      const column = assertFound(s.columns[operation.id], 'Column', operation.id)
      draft.putColumn({ ...column, notes: operation.notes })
      return
    }
    case 'column.setGroup': {
      const column = assertFound(s.columns[operation.id], 'Column', operation.id)
      if (operation.groupId !== null && !s.groups[operation.groupId]) {
        throw new DomainError('invalid_reference', 'Column group does not exist')
      }
      draft.putColumn({ ...column, groupId: operation.groupId })
      return
    }
    case 'column.move': {
      assertFound(s.columns[operation.id], 'Column', operation.id)
      draft.setColumnIds(insertBefore(s.columnIds, operation.id, operation.beforeId, 'the column order'))
      return
    }
    case 'column.setContext': {
      const column = assertFound(s.columns[operation.id], 'Column', operation.id)
      draft.putColumn({ ...column, context: operation.context })
      return
    }
    case 'column.delete': {
      assertFound(s.columns[operation.id], 'Column', operation.id)
      for (const rowId of Object.keys(s.rows)) {
        const ids = s.cardIdsByCell[cellKey(rowId, operation.id)]
        if (!ids) continue
        for (const cardId of ids) draft.drop('cards', cardId)
        draft.setCardIds(rowId, operation.id, [])
      }
      draft.drop('columns', operation.id)
      draft.setColumnIds(s.columnIds.filter((id) => id !== operation.id))
      return
    }

    // --- swimlanes ---------------------------------------------------------
    case 'swimlane.create': {
      const input = operation.swimlane
      sameBoard(boardId, input.boardId, 'Swimlane')
      if (s.swimlanes[input.id]) throw new DomainError('invalid_operation', 'Swimlane already exists')
      draft.putSwimlane({ ...input, position: 0 })
      draft.setSwimlaneIds(insertBefore(s.swimlaneIds, input.id, operation.beforeId, 'the swimlane order'))
      draft.setRowIds(input.id, [])
      return
    }
    case 'swimlane.rename': {
      const swimlane = assertFound(s.swimlanes[operation.id], 'Swimlane', operation.id)
      draft.putSwimlane({ ...swimlane, title: operation.title })
      return
    }
    case 'swimlane.setNotes': {
      const swimlane = assertFound(s.swimlanes[operation.id], 'Swimlane', operation.id)
      draft.putSwimlane({ ...swimlane, notes: operation.notes })
      return
    }
    case 'swimlane.move': {
      assertFound(s.swimlanes[operation.id], 'Swimlane', operation.id)
      draft.setSwimlaneIds(insertBefore(s.swimlaneIds, operation.id, operation.beforeId, 'the swimlane order'))
      return
    }
    case 'swimlane.setContext': {
      const swimlane = assertFound(s.swimlanes[operation.id], 'Swimlane', operation.id)
      draft.putSwimlane({ ...swimlane, context: operation.context })
      return
    }
    case 'swimlane.delete': {
      assertFound(s.swimlanes[operation.id], 'Swimlane', operation.id)
      for (const rowId of s.rowIdsBySwimlane[operation.id] ?? []) {
        deleteRowCascade(draft, rowId)
      }
      draft.dropRowIds(operation.id)
      draft.drop('swimlanes', operation.id)
      draft.setSwimlaneIds(s.swimlaneIds.filter((id) => id !== operation.id))
      return
    }

    // --- rows --------------------------------------------------------------
    case 'row.create': {
      const input = operation.row
      sameBoard(boardId, input.boardId, 'Row')
      if (s.rows[input.id]) throw new DomainError('invalid_operation', 'Row already exists')
      assertFound(s.swimlanes[input.swimlaneId], 'Swimlane', input.swimlaneId)
      draft.putRow({ ...input, position: 0 })
      const siblings = draft.state.rowIdsBySwimlane[input.swimlaneId] ?? []
      draft.setRowIds(input.swimlaneId, insertBefore(siblings, input.id, operation.beforeId, 'the swimlane'))
      return
    }
    case 'row.rename': {
      const row = assertFound(s.rows[operation.id], 'Row', operation.id)
      draft.putRow({ ...row, title: operation.title })
      return
    }
    case 'row.setNotes': {
      const row = assertFound(s.rows[operation.id], 'Row', operation.id)
      draft.putRow({ ...row, notes: operation.notes })
      return
    }
    case 'row.move': {
      const row = assertFound(s.rows[operation.id], 'Row', operation.id)
      assertFound(s.swimlanes[operation.swimlaneId], 'Swimlane', operation.swimlaneId)
      const from = row.swimlaneId
      const to = operation.swimlaneId
      if (from === to) {
        const siblings = draft.state.rowIdsBySwimlane[to] ?? []
        draft.setRowIds(to, insertBefore(siblings, operation.id, operation.beforeId, 'the swimlane'))
      } else {
        draft.setRowIds(from, (draft.state.rowIdsBySwimlane[from] ?? []).filter((id) => id !== operation.id))
        const siblings = draft.state.rowIdsBySwimlane[to] ?? []
        draft.setRowIds(to, insertBefore(siblings, operation.id, operation.beforeId, 'the swimlane'))
      }
      return
    }
    case 'row.setContext': {
      const row = assertFound(s.rows[operation.id], 'Row', operation.id)
      draft.putRow({ ...row, context: operation.context })
      return
    }
    case 'row.delete': {
      const row = assertFound(s.rows[operation.id], 'Row', operation.id)
      deleteRowCascade(draft, operation.id)
      const siblings = draft.state.rowIdsBySwimlane[row.swimlaneId] ?? []
      draft.setRowIds(row.swimlaneId, siblings.filter((id) => id !== operation.id))
      return
    }

    // --- cards -------------------------------------------------------------
    case 'card.create': {
      const input = operation.card
      sameBoard(boardId, input.boardId, 'Card')
      if (s.cards[input.id]) throw new DomainError('invalid_operation', 'Card already exists')
      assertFound(s.rows[input.rowId], 'Row', input.rowId)
      assertFound(s.columns[input.columnId], 'Column', input.columnId)
      // A new card is version 1; the field stays absent until a save so an undone delete
      // restores the card exactly as it was.
      draft.putCard({ ...input, position: 0, createdAt: input.createdAt ?? now, updatedAt: now })
      const siblings = draft.state.cardIdsByCell[cellKey(input.rowId, input.columnId)] ?? []
      draft.setCardIds(input.rowId, input.columnId, insertBefore(siblings, input.id, operation.beforeId, 'the cell'))
      return
    }
    case 'card.update': {
      const card = assertFound(s.cards[operation.id], 'Card', operation.id)
      // Every content save is a new version, including an undo: versions only go up.
      const changed = Object.keys(operation.patch).length > 0
      draft.putCard({ ...card, ...operation.patch, version: (card.version ?? 1) + (changed ? 1 : 0), updatedAt: now })
      return
    }
    case 'card.move': {
      const card = assertFound(s.cards[operation.id], 'Card', operation.id)
      assertFound(s.rows[operation.rowId], 'Row', operation.rowId)
      assertFound(s.columns[operation.columnId], 'Column', operation.columnId)
      const movedWithinCell = card.rowId === operation.rowId && card.columnId === operation.columnId
      if (!movedWithinCell) {
        const sourceIds = draft.state.cardIdsByCell[cellKey(card.rowId, card.columnId)] ?? []
        draft.setCardIds(card.rowId, card.columnId, sourceIds.filter((id) => id !== operation.id))
      }
      const targetIds = draft.state.cardIdsByCell[cellKey(operation.rowId, operation.columnId)] ?? []
      draft.setCardIds(
        operation.rowId,
        operation.columnId,
        insertBefore(targetIds, operation.id, operation.beforeId, 'the destination cell'),
      )
      draft.putCard({ ...draft.state.cards[operation.id]!, updatedAt: now })
      return
    }
    case 'card.delete': {
      const card = assertFound(s.cards[operation.id], 'Card', operation.id)
      draft.drop('cards', operation.id)
      const siblings = draft.state.cardIdsByCell[cellKey(card.rowId, card.columnId)] ?? []
      draft.setCardIds(card.rowId, card.columnId, siblings.filter((id) => id !== operation.id))
      return
    }

    // --- findings ----------------------------------------------------------
    case 'finding.create': {
      const input = operation.finding
      sameBoard(boardId, input.boardId, 'Finding')
      if (draft.state.findings[input.id]) throw new DomainError('invalid_operation', 'Finding already exists')
      validateFinding(draft.state, input)
      draft.putFinding({ ...input, status: 'open', createdAt: now, resolvedAt: null })
      return
    }
    case 'finding.resolve': {
      const finding = assertFound(draft.state.findings[operation.id], 'Finding', operation.id)
      draft.putFinding({ ...finding, status: operation.status, resolvedAt: operation.status === 'open' ? null : now })
      return
    }
  }
}

/** Remove a row and every card in it. Callers fix up the swimlane's row list. */
function deleteRowCascade(draft: Draft, rowId: string): void {
  const s = draft.state
  for (const columnId of s.columnIds) {
    const ids = s.cardIdsByCell[cellKey(rowId, columnId)]
    if (!ids) continue
    for (const cardId of ids) draft.drop('cards', cardId)
    draft.setCardIds(rowId, columnId, [])
  }
  draft.drop('rows', rowId)
}
